import React, { useState } from 'react';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useConfirm } from '../../shared/AdminUi';
import axios from 'axios';
import { Role } from '../../../../models/role';
import StandardModal, { useModalLoading } from '../../../../components/modales/StandardModal';
import PermissionButton from '../../../../components/common/PermissionButton';
import { shouldRenderForbiddenAction } from '../../../../utils/permission-ui';
import { useT } from '../../../../i18n';

interface RoleModalProps {
    open: boolean;
    onClose: () => void;
    roles: Role[];
    canCreate: boolean;
    canDelete: boolean;
    refreshRoles: () => void;
    showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
    showLoading: () => void;
    hideLoading: () => void;
}


const RoleModal: React.FC<RoleModalProps> = ({
    open,
    onClose,
    roles,
    canCreate,
    canDelete,
    refreshRoles,
    showAlert,
}) => {
    // Cargando propio del modal al crear o eliminar perfiles (el general queda detrás).
    const { loading, showLoading, hideLoading } = useModalLoading();
    const t = useT();
    const [newRoleName, setNewRoleName] = useState('');
    const showDeleteActions = shouldRenderForbiddenAction(canDelete);
    const [confirm, confirmDialog] = useConfirm();

    const handleCreateRole = async () => {
        if (!canCreate || !newRoleName.trim()) return;

        try {
            showLoading();
            await axios.post('roles', { name: newRoleName });
            showAlert(t('permissions.roleCreatedOk'), 'success');
            setNewRoleName('');
            refreshRoles();
            onClose();
        } catch (error) {
            showAlert(t('permissions.roleCreateError'), 'error');
        } finally {
            hideLoading();
        }
    };

    const handleDeleteRole = async (id: number, name: string) => {
        if (!canDelete) return;
        if (!(await confirm({ title: t('permissions.confirmDeleteRole', { name }), message: t('permissions.confirmDeleteRoleHint') }))) return;

        try {
            showLoading();
            await axios.delete(`roles/${id}`);
            showAlert(t('permissions.roleDeletedOk'), 'success');
            refreshRoles();
        } catch (error) {
            showAlert(t('permissions.roleDeleteError'), 'error');
        } finally {
            hideLoading();
        }
    };

    return (
        <>
        <StandardModal
            open={open}
            onClose={onClose}
            title={t('permissions.manageRoles')}
            maxWidth={600}
            loading={loading}
            actions={
                <>
                    <Button variant="outlined" color="inherit" onClick={onClose}>
                        {t('common.cancel')}
                    </Button>
                    <PermissionButton
                        allowed={canCreate}
                        deniedTooltip={t('common.noPermissionCreate')}
                        variant="contained"
                        onClick={handleCreateRole}
                        disabled={!newRoleName.trim()}
                    >
                        {t('permissions.createRole')}
                    </PermissionButton>
                </>
            }
        >
            <TextField
                id="new-role-name"
                fullWidth
                label={t('permissions.roleName')}
                helperText={t('permissions.roleNameHint')}
                value={newRoleName}
                onChange={(e) => setNewRoleName(e.target.value)}
            />

            <Typography variant="subtitle2" mt={3} mb={0.5}>
                {t('permissions.existingRoles')}
            </Typography>
            <List dense disablePadding sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
                {roles.map((role) => (
                    <ListItem
                        key={role.id}
                        divider
                        secondaryAction={
                            showDeleteActions ? (
                                <Tooltip title={canDelete ? t('common.delete') : t('common.noPermissionDelete')}>
                                    <span>
                                        <IconButton
                                            edge="end"
                                            color="error"
                                            aria-label={`${t('common.delete')} ${role.name}`}
                                            onClick={() => handleDeleteRole(role.id, role.name)}
                                            disabled={!canDelete}
                                        >
                                            <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            ) : undefined
                        }
                    >
                        <ListItemText primary={role.name} />
                    </ListItem>
                ))}
            </List>
        </StandardModal>
        {confirmDialog}
        </>
    );
};
export default RoleModal;
